
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserBusinessEntityEntity from 'Models/Security/Acl/UserBusinessEntityEntity';
import VisitorsBusinessEntityEntity from 'Models/Security/Acl/VisitorsBusinessEntityEntity';
import { IAcl } from 'Models/Security/IAcl';
import moment from 'moment';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeFetchManyToManyFunc,
	makeFetchOneToManyFunc,
	makeJoinEqualsFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import { quickRegister } from 'config/quickRegister';
import { AbnBusinessEntityDetail, AbnTypeCode } from 'Hooks/Api/apiBusinessNumber';
import { validateAbn, validateAcn } from 'Hooks/useAusBusinessNumber';
import { runInAction } from 'mobx';
import { AddressEntity, TrustEntityCodesEntity } from 'Models/Entities';
import { Dictionary } from 'Util/CustomTypes';
import { isPhone } from 'Validators/Functions/Custom';
import { isEmail } from 'Validators/Functions/Email';
import { store } from '../Store';

export const ProvideTrusteeAcnOptions = [
	{ value: 'Yes', display: 'Yes, I have an ACN' },
	{ value: 'No', display: 'No' },
];

export interface IBusinessEntityAttributes extends IModelAttributes {
	enabledForMonitor: boolean;
	trusteeName: string;
	trusteeACN: string;
	isActive: boolean;
	finance: boolean;
	enabledForApprove: boolean;
	phone: string;
	enabledForPPSR: boolean;
	supplyGnE: boolean;
	email: string;
	legacyID: number;
	hire: boolean;
	dateOfBirth: Date;
	abn: string;
	useOrganisationEmail: boolean;
	name: string;
	acn: string;
	enableEmailNotifications: boolean;
	monitorNotificationsEmail: string;
	summary: string;
	enableDrinksLicence: boolean;
	canUpdatePaymentMethod: boolean;

	atbFiles: Array<
		| Models.AtbFileEntity
		| Models.IAtbFileEntityAttributes
	>;
	address?: Models.AddressEntity |
	Models.IAddressEntityAttributes;
	applicationss: Array<
		| Models.ApplicationEntity
		| Models.IApplicationEntityAttributes
	>;
	businessEntityCreditReportSettingss: Array<
		| Models.BusinessEntityCreditReportSettingsEntity
		| Models.IBusinessEntityCreditReportSettingsEntityAttributes
	>;
	organisationId: string;
	organisation: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	customerss: Array<
		| Models.CustomerEntity
		| Models.ICustomerEntityAttributes
	>;
	esigntemplatess: Array<
		| Models.EsigntemplateEntity
		| Models.IEsigntemplateEntityAttributes
	>;
	logoss: Array<
		| Models.LogoEntity
		| Models.ILogoEntityAttributes
	>;
	paymentMethods: Array<
		| Models.BusinessEntityPaymentMethod
		| Models.IBusinessEntityPaymentMethodAttributes
	>;
	primaryOrganisation?: Models.OrganisationEntity |
	Models.IOrganisationEntityAttributes;
	spgss: Array<
		| Models.SpgEntity
		| Models.ISpgEntityAttributes
	>;
	searchRequests: Array<
		| Models.SearchRequestEntity
		| Models.ISearchRequestEntityAttributes
	>;
	users: Array<
		| Models.BusinessEntityUser
		| Models.IBusinessEntityUserAttributes
	>;
	integrationXeroTenantId?: string;
	integrationXeroTenantName?: string;
	automatedAtbProcessingDayOfMonth?: number;
}

@entity('BusinessEntity', 'Business Entity')
export default class BusinessEntity extends Model
	implements IBusinessEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsBusinessEntityEntity(),
		new UserBusinessEntityEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Enabled For Monitor',
		displayType: 'checkbox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enabledForMonitor: boolean;

	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Trustee Name',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public trusteeName: string;

	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Trustee ACN',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public trusteeACN: string;

	
	/**
	 * Is Active
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Is Active',
		displayType: 'checkbox',
		order: 60,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isActive: boolean;
	

	
	/**
	 * Is the business of type Finance
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Finance',
		displayType: 'checkbox',
		order: 70,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public finance: boolean;
	

	
	/**
	 * Enabled for Approve
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Enabled for Approve',
		displayType: 'checkbox',
		order: 80,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enabledForApprove: boolean;


	
	/**
	 * Phone
	 */
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Phone',
		displayType: 'textfield',
		order: 120,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public phone: string;
	

	
	/**
	 * Enabled for PPSR
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Enabled for PPSR',
		displayType: 'checkbox',
		order: 130,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enabledForPPSR: boolean;

	
	/**
	 * Is the business of type Supply Goods and Equipment
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Supply GnE',
		displayType: 'checkbox',
		order: 160,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public supplyGnE: boolean;
	

	
	/**
	 * Email
	 */
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Email',
		displayType: 'textfield',
		order: 170,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public email: string;
	

	
	/**
	 * Legacy ID
	 */
	@Validators.Integer()
	@Validators.Unique()
	@observable
	@attribute<BusinessEntity, number>()
	@CRUD({
		name: 'Legacy ID',
		displayType: 'textfield',
		order: 180,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public legacyID: number;
	

	
	/**
	 * Is the business of a Hire business type
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Hire',
		displayType: 'checkbox',
		order: 200,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public hire: boolean;

	
	/**
	 * DoB field uses for sole traders
	 */
	@observable
	@attribute<BusinessEntity, Date>()
	@CRUD({
		name: 'Date of Birth',
		displayType: 'datetimepicker',
		order: 220,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public dateOfBirth: Date;
	

	
	/**
	 * ABN
	 */
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'ABN',
		displayType: 'textfield',
		order: 230,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public abn: string;
	

	
	/**
	 * Boolean for determining to use the organisation email instead of the saved one.
	 */
	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Use Organisation Email',
		displayType: 'checkbox',
		order: 240,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public useOrganisationEmail: boolean;
	

	
	/**
	 * Name
	 */
	@Validators.Required()
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 250,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;

	
	/**
	 * ACN
	 */
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'ACN',
		displayType: 'textfield',
		order: 270,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public acn: string;

	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Enable Email Notifications',
		displayType: 'checkbox',
		order: 300,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enableEmailNotifications: boolean;

	
	@Validators.Email()
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Monitor Notifications Email',
		displayType: 'textfield',
		order: 320,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public monitorNotificationsEmail: string;
	

	
	/**
	 * Brief summary of the business
	 */
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Summary',
		displayType: 'textarea',
		order: 330,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public summary: string;

	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Enable Drinks Licence',
		displayType: 'checkbox',
		order: 350,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enableDrinksLicence: boolean;

	@observable
	@attribute<BusinessEntity, boolean>()
	@CRUD({
		name: 'Can Update Payment Method',
		displayType: 'checkbox',
		order: 360,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public canUpdatePaymentMethod: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ATB Files',
		displayType: 'reference-multicombobox',
		order: 370,
		referenceTypeFunc: () => Models.AtbFileEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atbFiles',
			oppositeEntity: () => Models.AtbFileEntity,
		}),
		
	})
	public atbFiles: Models.AtbFileEntity[] = [];

	/**
	 * Address
	 */
	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'Address',
		displayType: 'reference-combobox',
		order: 380,
		referenceTypeFunc: () => Models.AddressEntity,
		optionEqualFunc: (model, option) => model.id === option,
		inputProps: {
			fetchReferenceEntity: true,
		},
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'addresss',
			oppositeEntity: () => Models.AddressEntity,
		}),
		
	})
	public address?: Models.AddressEntity;

	/**
	 * Approve Applications
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Applicationss',
		displayType: 'reference-multicombobox',
		order: 390,
		referenceTypeFunc: () => Models.ApplicationEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'applicationss',
			oppositeEntity: () => Models.ApplicationEntity,
		}),
		
	})
	public applicationss: Models.ApplicationEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Business Entity Credit Report Settingss',
		displayType: 'reference-multicombobox',
		order: 400,
		referenceTypeFunc: () => Models.BusinessEntityCreditReportSettingsEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'businessEntityCreditReportSettingss',
			oppositeEntity: () => Models.BusinessEntityCreditReportSettingsEntity,
		}),
		
	})
	public businessEntityCreditReportSettingss: Models.BusinessEntityCreditReportSettingsEntity[] = [];

	@observable
	@attribute()
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 410,
		referenceTypeFunc: () => Models.OrganisationEntity,
		
	})
	public organisationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Customerss',
		displayType: 'reference-multicombobox',
		order: 420,
		referenceTypeFunc: () => Models.CustomerEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'customerss',
			oppositeEntity: () => Models.CustomerEntity,
		}),
		
	})
	public customerss: Models.CustomerEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignTemplatess',
		displayType: 'reference-multicombobox',
		order: 430,
		referenceTypeFunc: () => Models.EsigntemplateEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esigntemplatess',
			oppositeEntity: () => Models.EsigntemplateEntity,
		}),
		
	})
	public esigntemplatess: Models.EsigntemplateEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Logoss',
		displayType: 'reference-multicombobox',
		order: 440,
		referenceTypeFunc: () => Models.LogoEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'logoss',
			oppositeEntity: () => Models.LogoEntity,
		}),
		
	})
	public logoss: Models.LogoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Payment Method',
		displayType: 'reference-multicombobox',
		order: 450,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.BusinessEntityPaymentMethod,
		optionEqualFunc: makeJoinEqualsFunc('paymentMethodId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'businessEntity',
			oppositeEntityName: 'paymentMethodEntity',
			relationName: 'businessEntity',
			relationOppositeName: 'paymentMethod',
			entity: () => Models.BusinessEntity,
			joinEntity: () => Models.BusinessEntityPaymentMethod,
			oppositeEntity: () => Models.PaymentMethodEntity,
		}),
		
	})
	public paymentMethods: Models.BusinessEntityPaymentMethod[] = [];

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'Primary Organisation',
		displayType: 'reference-combobox',
		order: 460,
		referenceTypeFunc: () => Models.OrganisationEntity,
		optionEqualFunc: (model, option) => model.id === option,
		inputProps: {
			fetchReferenceEntity: true,
		},
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'primaryOrganisations',
			oppositeEntity: () => Models.OrganisationEntity,
		}),
		
	})
	public primaryOrganisation?: Models.OrganisationEntity;

	/**
	 * PPSR SPGs
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'SPGss',
		displayType: 'reference-multicombobox',
		order: 470,
		referenceTypeFunc: () => Models.SpgEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'spgss',
			oppositeEntity: () => Models.SpgEntity,
		}),
		
	})
	public spgss: Models.SpgEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Search Requests',
		displayType: 'reference-multicombobox',
		order: 480,
		referenceTypeFunc: () => Models.SearchRequestEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'searchRequests',
			oppositeEntity: () => Models.SearchRequestEntity,
		}),
		
	})
	public searchRequests: Models.SearchRequestEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'User',
		displayType: 'reference-multicombobox',
		order: 490,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.BusinessEntityUser,
		optionEqualFunc: makeJoinEqualsFunc('userId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'businessEntity',
			oppositeEntityName: 'userEntity',
			relationName: 'businessEntity',
			relationOppositeName: 'user',
			entity: () => Models.BusinessEntity,
			joinEntity: () => Models.BusinessEntityUser,
			oppositeEntity: () => Models.UserEntity,
		}),
		
	})
	public users: Models.BusinessEntityUser[] = [];


	// WHEN ADDING EXTRA ATTRIBUTES remember to add them to the clear fields function
	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Integration Xero Tenant Id',
		displayType: 'textfield',
		order: 430,
		searchable: false,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseString,
	})
	public integrationXeroTenantId?: string;

	@observable
	@attribute<BusinessEntity, string>()
	@CRUD({
		name: 'Integration Xero Tenant Name',
		displayType: 'textfield',
		order: 440,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public integrationXeroTenantName?: string;

	@Validators.Integer()
	@observable
	@attribute<BusinessEntity, number>()
	@CRUD({
		name: 'Integration Schedule Day Of Month',
		displayType: 'textfield',
		order: 450,
		headerColumn: true,
		searchable: false,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public automatedAtbProcessingDayOfMonth?: number;

	// If empty string radio button is empty, if No trusteeName needs to be filled, if Yes trusteeAcn needs to be filled
	@observable public useCorporateTrusteeACN: ('' | 'Yes' | 'No');

	// Calculate if useCorporateTrusteeAcn is yes, no, or unset based on the entity values
	public deriveUseCorporateTrusteeAcn(): '' | 'Yes' | 'No' {
		// We've set a trustee ACN so we have previously specified to use a corporate trustee acn
		if (this.trusteeACN != null && this.trusteeACN.length > 0) return 'Yes';
		// We've set a trustee name so we have previously specified to use a corporate trustee name
		if (this.trusteeName != null && this.trusteeName.length > 0) return 'No';
		// We havn't set an trustee acn or trusteename yet so its unknown
		return '';
	}

	/**
	 * Default organisational numbers for a new SPG: the ACN alone when present, otherwise the ABN and trustee ACN.
	 */
	public getDefaultSpgOrganisationalNumbers(): string {
		if (this.acn) return this.acn;
		return [this.abn, this.trusteeACN].filter(x => !!x).join(',');
	}

	// Assign abr response data to business entity fields
	public async prefillAbnFieldsFromAbrDetails(data: AbnBusinessEntityDetail) {

		// Can't do @action with async code
		const trusteeCodes = await TrustEntityCodesEntity.trusteeCodes();

		runInAction(() => {
			// If name has not been manually set, and there is an abn name available, use it
			if (!this.name && data.name) this.name = data.name;
			this.validateField('name');

			// If the provided ABN for a business entity has specific fields set if its sole trader or trustee or other
			if (data.typeCode === AbnTypeCode.Individual) {
				//@ts-ignore
				this.trusteeACN = null;
				this.trusteeName = '';
				// If the provided ABN is a trustee entity, clear solet rader fields
			} else if (trusteeCodes.includes(data.typeCode ?? "")) {
				//@ts-ignore
				this.dateOfBirth = null;
			} else {
				//@ts-ignore
				this.trusteeACN = null;
				this.trusteeName = '';
				//@ts-ignore
				this.dateOfBirth = null;
			}
		})
	}

	// Will validate a field, then update the errors object
	@action public validateField = async (field: string, options?: Dictionary<string>) => {
		switch (field) {
			// Only do length validation on the business number as the BusinessValidationModel handles the api validation
			case 'abn': {
				const abnErrors = validateAbn(this.abn, false);
				this.setFieldError('abn', abnErrors.length > 0, abnErrors[0] || 'Invalid ABN');
				break;
			}
			case 'acn': {
				const acnErrors = validateAcn(this.acn);
				this.setFieldError('acn', !!this.acn && acnErrors.length > 0, acnErrors[0] || 'Invalid ACN');
				break;
			}
			case 'trusteeACN': {
				const trusteeAcnErrors = validateAcn(this.trusteeACN);
				this.setFieldError('trusteeACN', !!this.trusteeACN && trusteeAcnErrors.length > 0, trusteeAcnErrors[0] || 'Invalid Trustee ACN');
				break;
			}
			case 'name': {
				this.setFieldError('name', !this.name, 'Organisation Name cannot be empty');
				break;
			}
			case 'phone': {
				this.setFieldError('phone', !!this.phone && !isPhone(this.phone), 'Phone Number must be valid');
				break;
			}
			case 'summary': {
				// Business description only need validation when using quick register
				if (!quickRegister) {
					this.setFieldError('summary', false, '');
					break;
				}

				const description = this.summary.trim();

				if (!description) {
					this.setFieldError('summary', true, 'Business description cannot be empty.');
					break;
				}

				if (description.length < 5) {
					this.setFieldError('summary', true, 'Business description must be at least 5 characters long.');
					break;
				}

				if (description.length > 500) {
					this.setFieldError('summary', true, 'Business description cannot exceed 500 characters.');
					break;
				}

				if (description === description.toUpperCase()) {
					this.setFieldError('summary', true, 'Business description should not be in all caps.');
					break;
				}

				const invalidCharPattern = /[<>%\\{}[\]()~^|*]/g;
				const invalidChars = description.match(invalidCharPattern);

				if (invalidChars && invalidChars.length > 0) {
					const uniqueInvalidChars = [...new Set(invalidChars)];
					const charList = uniqueInvalidChars.join(', ');
					this.setFieldError(
						'summary',
						true,
						`Business description contains invalid characters: ${charList}.`
					);
					break;
				}

				// Auto-format description (ensure the first letter is capitalised)
				this.summary = description.charAt(0).toUpperCase() + description.slice(1);
				this.setFieldError('summary', false, '');
				break;
			}
			case 'dateOfBirth': {
				if (this.dateOfBirth != null && moment(this.dateOfBirth).isAfter()) {
					this.setFieldError('dateOfBirth', true, 'Invalid Date of birth');
					break;
				}
				const abnEntityType = options ? options['abnEntityType'] : null;
				if (abnEntityType === AbnTypeCode.Individual && this.dateOfBirth == null) {
					this.setFieldError('dateOfBirth', true, 'Date of birth is required');
					break;
				}
				this.setFieldError('dateOfBirth', false, '');
				break;
			}
			// Email is required for legacyId to be set on creating business entity
			case 'email': {
				const hasEmailInput = this.email != null && this.email != "";
				const hasValidEmail = hasEmailInput && isEmail(this.email);

				if (hasValidEmail) {
					this.setFieldError('email', false, '');
					break;
				}
				if (hasEmailInput && !hasValidEmail) {
					this.setFieldError('email', true, 'Email Address is invalid');
					break;
				}
				if (!hasValidEmail && this.useOrganisationEmail != true) {
					this.setFieldError('email', true, 'Email Address is required');
					break;
				}
				break;
			}
			case 'ppsrEmail': {
				if (this.enabledForPPSR) {
					if (!this.spgss[0].email) {
						this.setFieldError('ppsrEmail', true, 'Email Address cannot be empty');
						break;
					}
					if (!isEmail(this.spgss[0].email)) {
						this.setFieldError('ppsrEmail', true, 'Email Address must be valid');
						break;
					}
				}
				this.setFieldError('ppsrEmail', false, '');
				break;
			}
			case 'approveEmail': {
				if (this.enabledForApprove) {
					if (!this.applicationss[0].email) {
						this.setFieldError('approveEmail', true, 'Email Address cannot be empty');
						break;
					}
					if (!isEmail(this.applicationss[0].email)) {
						this.setFieldError('approveEmail', true, 'Email Address must be valid');
						break;
					}
				}
				this.setFieldError('approveEmail', false, '');
				break;
			}
			case 'monitorNotificationsEmail': {
				if (!this.enabledForMonitor) {
					// Never validate this field
					this.setFieldError('monitorNotificationsEmail', false, '');
					break;
				}

				if (this.enableEmailNotifications && !this.monitorNotificationsEmail) {
					this.setFieldError('monitorNotificationsEmail', true, 'Email Address cannot be empty');
					break;
				}
				if (this.monitorNotificationsEmail && !isEmail(this.monitorNotificationsEmail)) {
					this.setFieldError('monitorNotificationsEmail', true, 'Email Address must be valid');
					break;
				}
				this.setFieldError('monitorNotificationsEmail', false, '');
				break;
			}
			case 'applicationName': {
				this.setFieldError(
					'applicationName',
					this.enabledForApprove && !this.applicationss[0].applicationName,
					'Application Name cannot be empty',
				);
				break;
			}
			case 'summary': {
				if (this.enabledForPPSR) {
					this.setFieldError('summary', !this.summary, 'Summary cannot be empty');
				}
				break;
			}
			case 'address.postcode': {
				// Borrow errors from the address entity
				await this.address?.validateField('postcode');
				break;
			}

		}
	};

	@action public validateReferralPartnerField = async (field: string) => {
		switch (field) {
			default: {
				await this.validateField(field);
				break;
			}
		}
	};

	public validateDetailsFields = async (additionalFields: string[] = [], options?: Dictionary<string>) => {
		const fieldList = [
			'abn', 'acn', 'trusteeACN',
			'name', 'phone', 'dateOfBirth', 'email', 'address.postcode',
			...additionalFields,
		];
		const validationPromises = fieldList.map(field => this.validateField(field, options));
		await Promise.all(validationPromises);
	};

	public validateReferralPartnerFields = async () => {
		const fieldList = [
			'name', 'phone', 'email',
		];
		const validationPromises = fieldList.map(field => this.validateReferralPartnerField(field));
		await Promise.all(validationPromises);
	};

	public validatePpsrFields = async () => {
		const fieldList = ['summary', 'ppsrEmail'];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	};

	public validateMonitorFields = async () => {
		const fieldList = ['monitorNotificationsEmail'];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	};

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IBusinessEntityAttributes>) {

		super(attributes);

		this.summary = attributes?.summary ?? ''; // Initialise the summary to an empty string if not provided
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IBusinessEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.enabledForMonitor !== undefined) {
				this.enabledForMonitor = attributes.enabledForMonitor;
			}

			if (attributes.trusteeName !== undefined) {
				this.trusteeName = attributes.trusteeName;
			}
			if (attributes.trusteeACN !== undefined) {
				this.trusteeACN = attributes.trusteeACN;
			}

			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.finance !== undefined) {
				this.finance = attributes.finance;
			}
			if (attributes.enabledForApprove !== undefined) {
				this.enabledForApprove = attributes.enabledForApprove;
			}

			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.enabledForPPSR !== undefined) {
				this.enabledForPPSR = attributes.enabledForPPSR;
			}

			if (attributes.supplyGnE !== undefined) {
				this.supplyGnE = attributes.supplyGnE;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.legacyID !== undefined) {
				this.legacyID = attributes.legacyID;
			}

			if (attributes.hire !== undefined) {
				this.hire = attributes.hire;
			}

			if (attributes.dateOfBirth !== undefined) {
				if (attributes.dateOfBirth === null) {
					this.dateOfBirth = attributes.dateOfBirth;
				} else {
					this.dateOfBirth = moment(attributes.dateOfBirth).toDate();
				}
			}
			if (attributes.abn !== undefined) {
				this.abn = attributes.abn;
			}
			if (attributes.useOrganisationEmail !== undefined) {
				this.useOrganisationEmail = attributes.useOrganisationEmail;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}

			if (attributes.acn !== undefined) {
				this.acn = attributes.acn;
			}


			if (attributes.enableEmailNotifications !== undefined) {
				this.enableEmailNotifications = attributes.enableEmailNotifications;
			}

			if (attributes.monitorNotificationsEmail !== undefined) {
				this.monitorNotificationsEmail = attributes.monitorNotificationsEmail;
			}
			if (attributes.summary !== undefined) {
				this.summary = attributes.summary;
			}

			if (attributes.enableDrinksLicence !== undefined) {
				this.enableDrinksLicence = attributes.enableDrinksLicence;
			}
			if (attributes.canUpdatePaymentMethod !== undefined) {
				this.canUpdatePaymentMethod = attributes.canUpdatePaymentMethod;
			}
			if (attributes.atbFiles !== undefined && Array.isArray(attributes.atbFiles)) {
				for (const model of attributes.atbFiles) {
					if (model instanceof Models.AtbFileEntity) {
						this.atbFiles.push(model);
					} else {
						this.atbFiles.push(new Models.AtbFileEntity(model));
					}
				}
			}
			if (attributes.address !== undefined) {
				if (attributes.address === null) {
					this.address = attributes.address;
				} else if (attributes.address instanceof Models.AddressEntity) {
					this.address = attributes.address;
				} else {
					this.address = new Models.AddressEntity(attributes.address);
				}
			}
			if (attributes.applicationss !== undefined && Array.isArray(attributes.applicationss)) {
				for (const model of attributes.applicationss) {
					if (model instanceof Models.ApplicationEntity) {
						this.applicationss.push(model);
					} else {
						this.applicationss.push(new Models.ApplicationEntity(model));
					}
				}
			}
			if (attributes.businessEntityCreditReportSettingss !== undefined && Array.isArray(attributes.businessEntityCreditReportSettingss)) {
				for (const model of attributes.businessEntityCreditReportSettingss) {
					if (model instanceof Models.BusinessEntityCreditReportSettingsEntity) {
						this.businessEntityCreditReportSettingss.push(model);
					} else {
						this.businessEntityCreditReportSettingss.push(new Models.BusinessEntityCreditReportSettingsEntity(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}
			if (attributes.customerss !== undefined && Array.isArray(attributes.customerss)) {
				for (const model of attributes.customerss) {
					if (model instanceof Models.CustomerEntity) {
						this.customerss.push(model);
					} else {
						this.customerss.push(new Models.CustomerEntity(model));
					}
				}
			}
			if (attributes.esigntemplatess !== undefined && Array.isArray(attributes.esigntemplatess)) {
				for (const model of attributes.esigntemplatess) {
					if (model instanceof Models.EsigntemplateEntity) {
						this.esigntemplatess.push(model);
					} else {
						this.esigntemplatess.push(new Models.EsigntemplateEntity(model));
					}
				}
			}
			if (attributes.logoss !== undefined && Array.isArray(attributes.logoss)) {
				for (const model of attributes.logoss) {
					if (model instanceof Models.LogoEntity) {
						this.logoss.push(model);
					} else {
						this.logoss.push(new Models.LogoEntity(model));
					}
				}
			}
			if (attributes.paymentMethods !== undefined && Array.isArray(attributes.paymentMethods)) {
				for (const model of attributes.paymentMethods) {
					if (model instanceof Models.BusinessEntityPaymentMethod) {
						this.paymentMethods.push(model);
					} else {
						this.paymentMethods.push(new Models.BusinessEntityPaymentMethod(model));
					}
				}
			}
			if (attributes.primaryOrganisation !== undefined) {
				if (attributes.primaryOrganisation === null) {
					this.primaryOrganisation = attributes.primaryOrganisation;
				} else if (attributes.primaryOrganisation instanceof Models.OrganisationEntity) {
					this.primaryOrganisation = attributes.primaryOrganisation;
				} else {
					this.primaryOrganisation = new Models.OrganisationEntity(attributes.primaryOrganisation);
				}
			}
			if (attributes.spgss !== undefined && Array.isArray(attributes.spgss)) {
				for (const model of attributes.spgss) {
					if (model instanceof Models.SpgEntity) {
						this.spgss.push(model);
					} else {
						this.spgss.push(new Models.SpgEntity(model));
					}
				}
			}
			if (attributes.searchRequests !== undefined && Array.isArray(attributes.searchRequests)) {
				for (const model of attributes.searchRequests) {
					if (model instanceof Models.SearchRequestEntity) {
						this.searchRequests.push(model);
					} else {
						this.searchRequests.push(new Models.SearchRequestEntity(model));
					}
				}
			}
			if (attributes.users !== undefined && Array.isArray(attributes.users)) {
				for (const model of attributes.users) {
					if (model instanceof Models.BusinessEntityUser) {
						this.users.push(model);
					} else {
						this.users.push(new Models.BusinessEntityUser(model));
					}
				}
			}

			if (attributes.integrationXeroTenantId !== undefined) {
				this.integrationXeroTenantId = attributes.integrationXeroTenantId;
			}
			if (attributes.integrationXeroTenantName !== undefined) {
				this.integrationXeroTenantName = attributes.integrationXeroTenantName;
			}
			if (attributes.automatedAtbProcessingDayOfMonth !== undefined) {
				this.automatedAtbProcessingDayOfMonth = attributes.automatedAtbProcessingDayOfMonth;
			}

			// Needs to be called after assiging trusteeAscn/trusteeName
			this.useCorporateTrusteeACN = this.deriveUseCorporateTrusteeAcn();
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		paymentMethods {
			${Models.BusinessEntityPaymentMethod.getAttributes().join('\n')}
			paymentMethod {
				${Models.PaymentMethodEntity.getAttributes().join('\n')}
			}
		}
		users {
			${Models.BusinessEntityUser.getAttributes().join('\n')}
			user {
				${Models.UserEntity.getAttributes().join('\n')}
			}
		}
		atbFiles {
			${Models.AtbFileEntity.getAttributes().join('\n')}
		}
		address {
			${Models.AddressEntity.getAttributes().join('\n')}
		}
		applicationss {
			${Models.ApplicationEntity.getAttributes().join('\n')}
			${Models.ApplicationEntity.getFiles().map(f => f.name).join('\n')}
		}
		businessEntityCreditReportSettingss {
			${Models.BusinessEntityCreditReportSettingsEntity.getAttributes().join('\n')}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
		customerss {
			${Models.CustomerEntity.getAttributes().join('\n')}
		}
		esigntemplatess {
			${Models.EsigntemplateEntity.getAttributes().join('\n')}
		}
		logoss {
			${Models.LogoEntity.getAttributes().join('\n')}
			${Models.LogoEntity.getFiles().map(f => f.name).join('\n')}
		}
		primaryOrganisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
		spgss {
			${Models.SpgEntity.getAttributes().join('\n')}
		}
		searchRequests {
			${Models.SearchRequestEntity.getAttributes().join('\n')}
			${Models.SearchRequestEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			paymentMethods: {},
			users: {},
			atbFiles: {},
			address: {},
			applicationss: {},
			businessEntityCreditReportSettingss: {},
			customerss: {},
			esigntemplatess: {},
			logoss: {},
			primaryOrganisation: {},
			spgss: {},
			searchRequests: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'address',
							'applicationss',
							'esigntemplatess',
							'logoss',
							'paymentMethods',
							'primaryOrganisation',
							'spgss',
							'users',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.name;
	}

	public static IsLinkedToUser(businessEntity: BusinessEntity): boolean {
		return BusinessEntity.IsLinkedToUserById(businessEntity.id);
	}

	public static IsLinkedToUserById(businessId: string): boolean {
		const { commonViewBusinessEntities } = store.userPermissions;
		if (commonViewBusinessEntities === 'NONE') return false;
		if (commonViewBusinessEntities === 'ALL') return true;
		if (commonViewBusinessEntities === 'WITHIN_ORGANISATION') return true;
		return store.getUser?.businessEntityIds?.includes(businessId) ?? false;
	}

	// Use ts-ignore because the C#Bot model doesn't type these as nullable, despite the fact that they don't have a
	// required attribute.
	@action public async removePpsrProduct() {
		// @ts-ignore
		this.hire = null;
		// @ts-ignore
		this.supplyGnE = null;
		// @ts-ignore
		this.finance = null;
		// @ts-ignore
		this.summary = null;
		this.enabledForPPSR = false;
		const promises = [];
		for (const reference of this.spgss) {
			reference.isActive = false;
			promises.push(reference.save());
		}
		await Promise.all(promises);
	}

	@action public async enableMonitorProduct() {
		this.enabledForMonitor = true;
		await this.save();
	}

	@action public async removeMonitorProduct() {
		this.enabledForMonitor = false;
		await this.save();
	}

	@action public async removeApproveProduct() {
		this.enabledForApprove = false;
		const promises = [];
		for (const reference of this.applicationss) {
			reference.isActive = false;
			promises.push(reference.save());
		}
		await Promise.all(promises);
	}

	// function to clear all the fields of a business entity. Including the extra observable fields
	@action public async clearFields() {
		const attributes = Models.BusinessEntity.getAttributes();
		attributes.forEach(attr => {
			this[attr] = undefined;
		});
		this.address = new AddressEntity();
		this.errors = {};
		this.integrationXeroTenantId = undefined;
		this.integrationXeroTenantName = undefined;
		this.automatedAtbProcessingDayOfMonth = undefined;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(BusinessEntity.prototype, 'created');
CRUD(modifiedAttr)(BusinessEntity.prototype, 'modified');
